import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAlert } from '../contexts/AlertContext';
import './QuestionManagementPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const QuestionManagementPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showWarning, showInfo } = useAlert();
  const [questions, setQuestions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);  // 전체 문제 개수
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // 페이징 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);  // 페이지당 문제 수
  
  // 필터링 상태
  const [filters, setFilters] = useState({
    book_id: '',
    difficulty: '',
    language: '',  // 언어 필터 추가
    is_active: '',
    admin_reviewed: ''
  });

  // 통계 상태
  const [statistics, setStatistics] = useState({});
  const [showStatisticsModal, setShowStatisticsModal] = useState(false);
  const [loadingStatistics, setLoadingStatistics] = useState(false);

  useEffect(() => {
    // 관리자 권한 확인
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    
    if (!token || userType !== 'admin') {
      navigate('/admin/login');
      return;
    }

    loadData();
  }, [navigate]);

  // 모달이 닫힐 때 body 스크롤 복원
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const loadData = async () => {
    try {
      await Promise.all([
        loadQuestions(),
        loadBooks()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      // 페이징 파라미터 추가
      const skip = (currentPage - 1) * pageSize;
      params.append('skip', skip.toString());
      params.append('limit', pageSize.toString());

      // v2 API 호출
      const response = await axios.get(
        `${API_BASE_URL}/api/v2/assessments/questions/management?${params.toString()}`
      );
      
      // 백엔드 응답 형태 확인
      if (Array.isArray(response.data)) {
        // 구 버전 API (배열 반환) - 백엔드 재시작 전
        setTotalCount(response.data.length);
        setQuestions(response.data);
      } else {
        // 신 버전 API (객체 반환) - 백엔드 재시작 후
        setTotalCount(response.data.total_count || 0);
        setQuestions(response.data.questions || []);
      }
    } catch (error) {
      console.error('❌ Error loading questions:', error);
      console.error('❌ Error response:', error.response?.data);
      setTotalCount(0);
      setQuestions([]);
    }
  };

  const loadBooks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/books/`);
      setBooks(response.data);
    } catch (error) {
      console.error('Error loading books:', error);
    }
  };

  const loadStatistics = async (bookId) => {
    try {
      setLoadingStatistics(true);
      setShowStatisticsModal(true); // 모달을 먼저 표시
      
      // 모바일에서 body 스크롤 막기
      document.body.style.overflow = 'hidden';
      
      const response = await axios.get(`${API_BASE_URL}/api/books/${bookId}/statistics`);
      setStatistics(response.data);
      console.log('Statistics loaded successfully:', response.data);
    } catch (error) {
      console.error('Error loading statistics:', error);
      setShowStatisticsModal(false); // 에러 시 모달 닫기
      
      // body 스크롤 복원
      document.body.style.overflow = 'auto';
      
      showError(`통계를 불러오는 중 오류가 발생했습니다: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoadingStatistics(false);
    }
  };

  const closeStatisticsModal = () => {
    setShowStatisticsModal(false);
    setLoadingStatistics(false);
    
    // body 스크롤 복원
    document.body.style.overflow = 'auto';
  };

  const handleFilterChange = (key, value) => {
    setFilters({
      ...filters,
      [key]: value
    });
  };

  const applyFilters = () => {
    setCurrentPage(1);  // 필터 적용 시 첫 페이지로
    loadQuestions();
  };

  const resetFilters = () => {
    setFilters({
      book_id: '',
      difficulty: '',
      language: '',
      is_active: '',
      admin_reviewed: ''
    });
    setCurrentPage(1);  // 필터 초기화 시 첫 페이지로
    setTimeout(() => loadQuestions(), 100);
  };

  // 페이지 변경 핸들러
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // 페이지 크기 변경 핸들러
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);  // 페이지 크기 변경 시 첫 페이지로
  };

  // currentPage, pageSize 변경 시 자동으로 데이터 로드
  useEffect(() => {
    if (!loading) {
      loadQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  const handleSelectQuestion = (questionId) => {
    setSelectedQuestions(prev => 
      prev.includes(questionId) 
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleSelectAll = () => {
    if (selectedQuestions.length === questions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(questions.map(q => q.id));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedQuestions.length === 0) {
      showWarning('선택된 문제가 없습니다.');
      return;
    }

    const confirmMessage = {
      'activate': '선택된 문제들을 활성화하시겠습니까?',
      'deactivate': '선택된 문제들을 비활성화하시겠습니까?',
      'mark_reviewed': '선택된 문제들을 검토완료로 표시하시겠습니까?',
      'delete': '선택된 문제들을 삭제하시겠습니까? (되돌릴 수 없습니다)'
    };

    if (!window.confirm(confirmMessage[action])) {
      return;
    }

    try {
      // v2 API 호출
      await axios.post(`${API_BASE_URL}/api/v2/assessments/questions/bulk-action`, {
        question_ids: selectedQuestions,
        action: action
      });

      setSelectedQuestions([]);
      loadQuestions();
      showSuccess('v2 문항 작업이 완료되었습니다.');
    } catch (error) {
      console.error('Error performing v2 bulk action:', error);
      showError('v2 문항 작업 중 오류가 발생했습니다.');
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion({ ...question });
    setShowEditModal(true);
  };

  const handleSaveQuestion = async () => {
    try {
      // v2 API 호출 (option_e는 v2에서 사용 안 함)
      await axios.put(`${API_BASE_URL}/api/v2/assessments/questions/${editingQuestion.id}`, {
        question_text: editingQuestion.question_text,
        correct_answer: editingQuestion.correct_answer,
        option_a: editingQuestion.option_a,
        option_b: editingQuestion.option_b,
        option_c: editingQuestion.option_c,
        option_d: editingQuestion.option_d,
        explanation: editingQuestion.explanation,
        hint_1: editingQuestion.hint_1,
        hint_2: editingQuestion.hint_2,
        hint_3: editingQuestion.hint_3,
        is_active: editingQuestion.is_active,
        admin_reviewed: editingQuestion.admin_reviewed
      });

      setShowEditModal(false);
      setEditingQuestion(null);
      loadQuestions();
      showSuccess('v2 문제가 수정되었습니다.');
    } catch (error) {
      console.error('Error updating v2 question:', error);
      showError('v2 문제 수정 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('이 v2 문제를 삭제하시겠습니까? (되돌릴 수 없습니다)')) {
      return;
    }

    try {
      // v2 API 호출
      await axios.delete(`${API_BASE_URL}/api/v2/assessments/questions/${questionId}`);
      loadQuestions();
      showSuccess('v2 문제가 삭제되었습니다.');
    } catch (error) {
      console.error('Error deleting v2 question:', error);
      showError('v2 문제 삭제 중 오류가 발생했습니다.');
    }
  };

  const getDifficultyLabel = (setId) => {
    // v2: SET_ID 기반 레이블
    const labels = {
      'SET1_FOUNDATION': '🏗️ Foundation (L1-L2)',
      'SET2_COMPREHENSION': '📖 Comprehension (L3-L4)',
      'SET3_TRANSFER': '🚀 Transfer (L5-L6)',
      'SET4_STORY_TRACKING': '📚 Story Tracking',
      'PLACEMENT': '🎯 Placement Test'
    };
    return labels[setId] || setId;
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading-center">
          <div className="loading-spinner"></div>
          <p>문제 목록을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container question-management">
      <div className="page-header">
        <h1>🔧 문제 관리</h1>
        <p>AI가 생성한 문제들을 검토하고 관리할 수 있습니다</p>
      </div>

      {/* 필터 섹션 */}
      <div className="card filters-card">
        <h3>📋 필터링</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>책 선택</label>
            <select
              value={filters.book_id}
              onChange={(e) => handleFilterChange('book_id', e.target.value)}
            >
              <option value="">모든 책</option>
              {books.map(book => (
                <option key={book.id} value={book.id}>
                  {book.title} - {book.author}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>평가 세트</label>
            <select
              value={filters.difficulty}
              onChange={(e) => handleFilterChange('difficulty', e.target.value)}
            >
              <option value="">모든 세트</option>
              <option value="SET1_FOUNDATION">🏗️ Foundation (L1-L2)</option>
              <option value="SET2_COMPREHENSION">📖 Comprehension (L3-L4)</option>
              <option value="SET3_TRANSFER">🚀 Transfer (L5-L6)</option>
              <option value="SET4_STORY_TRACKING">📚 Story Tracking</option>
              <option value="PLACEMENT">🎯 Placement Test</option>
            </select>
          </div>

          <div className="filter-group">
            <label>언어</label>
            <select
              value={filters.language}
              onChange={(e) => handleFilterChange('language', e.target.value)}
            >
              <option value="">모든 언어</option>
              <option value="ko">🇰🇷 한국어</option>
              <option value="en">🇺🇸 영어</option>
              <option value="zh">🇨🇳 중국어</option>
              <option value="ja">🇯🇵 일본어</option>
            </select>
          </div>

          <div className="filter-group">
            <label>활성 상태</label>
            <select
              value={filters.is_active}
              onChange={(e) => handleFilterChange('is_active', e.target.value)}
            >
              <option value="">전체</option>
              <option value="true">활성화</option>
              <option value="false">비활성화</option>
            </select>
          </div>

          <div className="filter-group">
            <label>검토 상태</label>
            <select
              value={filters.admin_reviewed}
              onChange={(e) => handleFilterChange('admin_reviewed', e.target.value)}
            >
              <option value="">전체</option>
              <option value="true">검토완료</option>
              <option value="false">검토대기</option>
            </select>
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={applyFilters} className="btn btn-primary">
            🔍 필터 적용
          </button>
          <button onClick={resetFilters} className="btn btn-secondary">
            🔄 초기화
          </button>
        </div>
      </div>



      {/* 일괄 작업 */}
      {selectedQuestions.length > 0 && (
        <div className="card bulk-actions-card">
          <h3>📋 일괄 작업 ({selectedQuestions.length}개 선택됨)</h3>
          <div className="bulk-actions">
            <button 
              onClick={() => handleBulkAction('activate')}
              className="btn btn-success btn-sm"
            >
              ✅ 활성화
            </button>
            <button 
              onClick={() => handleBulkAction('deactivate')}
              className="btn btn-warning btn-sm"
            >
              ⏸️ 비활성화
            </button>
            <button 
              onClick={() => handleBulkAction('mark_reviewed')}
              className="btn btn-info btn-sm"
            >
              ✔️ 검토완료
            </button>
            <button 
              onClick={() => handleBulkAction('delete')}
              className="btn btn-danger btn-sm"
            >
              🗑️ 삭제
            </button>
          </div>
        </div>
      )}

      {/* 문제 목록 */}
      <div className="card questions-card">
        <div className="questions-header">
          <h3>📝 문제 목록 (전체 {totalCount}개)</h3>
          <div className="questions-header-actions">
            <label className="select-all">
              <input
                type="checkbox"
                checked={selectedQuestions.length === questions.length && questions.length > 0}
                onChange={handleSelectAll}
              />
              전체 선택
            </label>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="page-size-select"
            >
              <option value={20}>20개씩</option>
              <option value={50}>50개씩</option>
              <option value={100}>100개씩</option>
              <option value={200}>200개씩</option>
            </select>
          </div>
        </div>

        {questions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📝</div>
            <h4>문제가 없습니다</h4>
            <p>필터 조건을 변경하거나 새 문제를 생성해보세요</p>
          </div>
        ) : (
          <div className="questions-list">
            {questions.map(question => (
              <div key={question.id} className="question-item">
                <div className="question-select">
                  <input
                    type="checkbox"
                    checked={selectedQuestions.includes(question.id)}
                    onChange={() => handleSelectQuestion(question.id)}
                  />
                </div>

                <div className="question-info">
                  <div className="question-meta">
                    <span className="book-title">{question.book_title}</span>
                    <span className={`difficulty-badge difficulty-${question.difficulty}`}>
                      {getDifficultyLabel(question.difficulty)}
                    </span>
                    {question.language && (
                      <span className="language-badge">
                        {question.language === 'ko' ? '🇰🇷 한국어' : 
                         question.language === 'en' ? '🇺🇸 영어' : 
                         question.language === 'zh' ? '🇨🇳 중국어' : 
                         question.language === 'ja' ? '🇯🇵 일본어' : question.language}
                      </span>
                    )}
                    <span className={`status-badge ${question.is_active ? 'active' : 'inactive'}`}>
                      {question.is_active ? '🟢 활성' : '🔴 비활성'}
                    </span>
                    <span className={`review-badge ${question.admin_reviewed ? 'reviewed' : 'pending'}`}>
                      {question.admin_reviewed ? '✅ 검토완료' : '⏳ 검토대기'}
                    </span>
                  </div>
                  
                  <div className="question-text">
                    {question.question_text}
                  </div>

                  {question.question_type === 'multiple_choice' && question.correct_answer && (
                    <div className="correct-answer">
                      <strong>정답:</strong> {question.correct_answer}
                    </div>
                  )}

                  <div className="question-date">
                    생성일: {new Date(question.created_at).toLocaleDateString('ko-KR')}
                  </div>
                </div>

                <div className="question-actions">
                  <button
                    onClick={() => loadStatistics(question.book_id)}
                    className="btn btn-info btn-sm"
                    title="통계 보기"
                    disabled={loadingStatistics}
                  >
                    {loadingStatistics ? '⏳' : '📊'}
                  </button>
                  <button
                    onClick={() => handleEditQuestion(question)}
                    className="btn btn-primary btn-sm"
                    title="수정"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDeleteQuestion(question.id)}
                    className="btn btn-danger btn-sm"
                    title="삭제"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 페이지네이션 */}
        {totalCount > 0 && (
          <div className="pagination">
            <div className="pagination-info">
              {((currentPage - 1) * pageSize + 1)} - {Math.min(currentPage * pageSize, totalCount)} / 전체 {totalCount}개
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="btn btn-secondary btn-sm"
              >
                ⏮️ 처음
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn btn-secondary btn-sm"
              >
                ◀️ 이전
              </button>
              <span className="page-numbers">
                {(() => {
                  const totalPages = Math.ceil(totalCount / pageSize);
                  const maxButtons = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
                  let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                  
                  if (endPage - startPage + 1 < maxButtons) {
                    startPage = Math.max(1, endPage - maxButtons + 1);
                  }
                  
                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        className={`btn ${i === currentPage ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                className="btn btn-secondary btn-sm"
              >
                다음 ▶️
              </button>
              <button
                onClick={() => handlePageChange(Math.ceil(totalCount / pageSize))}
                disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                className="btn btn-secondary btn-sm"
              >
                마지막 ⏭️
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 통계 모달 */}
      {showStatisticsModal && (
        <div className="modal-overlay" onClick={closeStatisticsModal}>
          <div className="modal statistics-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📊 {loadingStatistics ? '통계 로딩 중...' : `${statistics.book_title} 통계`}</h3>
              <button 
                onClick={closeStatisticsModal}
                className="modal-close"
                disabled={loadingStatistics}
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              {loadingStatistics ? (
                <div className="loading-center">
                  <div className="loading-spinner"></div>
                  <p>통계를 불러오는 중...</p>
                </div>
              ) : (
                <div className="statistics-grid">
                  {Object.entries(statistics.statistics || {}).map(([difficulty, stats]) => (
                    <div key={difficulty} className="stat-card">
                      <h4>{getDifficultyLabel(difficulty)}</h4>
                      <div className="stat-details">
                        <div className="stat-item">
                          <span className="stat-number">{stats.total}</span>
                          <span className="stat-label">총 문제</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-number">{stats.active}</span>
                          <span className="stat-label">활성</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-number">{stats.reviewed}</span>
                          <span className="stat-label">검토완료</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {Object.keys(statistics.statistics || {}).length > 0 && (
                    <div className="stat-summary">
                      <h4>📝 전체 요약</h4>
                      <p>총 문제 수: <strong>{statistics.total_questions || 0}개</strong></p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 문제 수정 모달 */}
      {showEditModal && editingQuestion && (
        <div className="modal-overlay">
          <div className="modal question-edit-modal">
            <div className="modal-header">
              <h3>✏️ 문제 수정</h3>
              <button 
                onClick={() => setShowEditModal(false)}
                className="modal-close"
              >
                ✕
              </button>
            </div>

            <div className="modal-content">
              <div className="form-group">
                <label>문제 내용</label>
                <textarea
                  value={editingQuestion.question_text}
                  onChange={(e) => setEditingQuestion({
                    ...editingQuestion,
                    question_text: e.target.value
                  })}
                  rows={3}
                  className="form-input"
                />
              </div>

              {editingQuestion.question_type === 'multiple_choice' && (
                <>
                  <div className="form-group">
                    <label>정답</label>
                    <select
                      value={editingQuestion.correct_answer || ''}
                      onChange={(e) => setEditingQuestion({
                        ...editingQuestion,
                        correct_answer: e.target.value
                      })}
                      className="form-input"
                    >
                      <option value="">선택하세요</option>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                      <option value="E">E</option>
                    </select>
                  </div>

                  {['A', 'B', 'C', 'D', 'E'].map(option => (
                    <div key={option} className="form-group">
                      <label>선택지 {option}</label>
                      <input
                        type="text"
                        value={editingQuestion[`option_${option.toLowerCase()}`] || ''}
                        onChange={(e) => setEditingQuestion({
                          ...editingQuestion,
                          [`option_${option.toLowerCase()}`]: e.target.value
                        })}
                        className="form-input"
                      />
                    </div>
                  ))}
                </>
              )}

              <div className="form-group">
                <label>해설</label>
                <textarea
                  value={editingQuestion.explanation || ''}
                  onChange={(e) => setEditingQuestion({
                    ...editingQuestion,
                    explanation: e.target.value
                  })}
                  rows={3}
                  className="form-input"
                />
              </div>

              {[1, 2, 3].map(num => (
                <div key={num} className="form-group">
                  <label>힌트 {num}</label>
                  <textarea
                    value={editingQuestion[`hint_${num}`] || ''}
                    onChange={(e) => setEditingQuestion({
                      ...editingQuestion,
                      [`hint_${num}`]: e.target.value
                    })}
                    rows={2}
                    className="form-input"
                    placeholder={
                      num === 1 ? '가벼운 힌트' :
                      num === 2 ? '중간 힌트' : '구체적인 힌트'
                    }
                  />
                </div>
              ))}

              <div className="form-row">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={editingQuestion.is_active}
                      onChange={(e) => setEditingQuestion({
                        ...editingQuestion,
                        is_active: e.target.checked
                      })}
                    />
                    활성화
                  </label>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={editingQuestion.admin_reviewed}
                      onChange={(e) => setEditingQuestion({
                        ...editingQuestion,
                        admin_reviewed: e.target.checked
                      })}
                    />
                    검토 완료
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={handleSaveQuestion} className="btn btn-primary">
                💾 저장
              </button>
              <button 
                onClick={() => setShowEditModal(false)}
                className="btn btn-secondary"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionManagementPage; 